import { db } from './Database';

export interface UnitLoadPosition {
    id?: number;
    palletLpnNumberId: number;
    variant: string;
    sideAPosition: string;
    sideBPosition: string;
    sideCPosition: string;
    sideDPosition: string;
    createdAt?: string;
    updatedAt?: string;
}

export class UnitLoadPositionService {
    // Get unit load position details for a pallet and variant
    static async getUnitLoadPositionDetails(palletId: number, variant: string): Promise<{ palletLpnNumbers: any[] }> {
        try {
            // First get all LPN numbers for the pallet
            const palletLpnNumbers = await db.palletLpnNumbers
                .where('palletId')
                .equals(palletId)
                .toArray();

            if (!palletLpnNumbers || palletLpnNumbers.length === 0) {
                throw new Error('Pallet LPN Numbers not found');
            }

            // Get unit load positions for each LPN number
            const response = await Promise.all(palletLpnNumbers.map(async palletLpnNumber => {
                const positions = await db.unitLoadPositions
                    .where('palletLpnNumberId')
                    .equals(palletLpnNumber.id!)
                    .filter(pos => pos.variant === variant)
                    .toArray();

                return {
                    ...palletLpnNumber,
                    unitLoadPositions: positions || []
                };
            }));

            return {
                palletLpnNumbers: response
            };
        } catch (error) {
            console.error('Error getting unit load position details:', error);
            throw error;
        }
    }

    // Create or update unit load positions
    static async createUnitLoadPositions(data: { palletId: number, palletLpnNumbers: any[] }): Promise<{ palletLpnNumbers: any[] }> {
        const { palletId, palletLpnNumbers } = data;

        return await db.transaction('rw', [db.palletLpnNumbers, db.unitLoadPositions], async () => {
            try {
                const createdOrUpdatedPositions = [];
                const timestamp = new Date().toISOString();

                for (const palletLpnNumber of palletLpnNumbers) {
                    const { id, lpnNo, endStatusPallet, unitLoadPositions, ...palletLpnNumberData } = palletLpnNumber;

                    // Create or update PalletLpnNumber
                    let palletLpnNumberRecord;
                    if (id) {
                        await db.palletLpnNumbers.update(id, {
                            lpnNo,
                            endStatusPallet,
                            palletId,
                            ...palletLpnNumberData,
                            updatedAt: timestamp
                        });
                        palletLpnNumberRecord = await db.palletLpnNumbers.get(id);
                    } else {
                        const newId = await db.palletLpnNumbers.add({
                            lpnNo,
                            endStatusPallet,
                            palletId,
                            ...palletLpnNumberData,
                            createdAt: timestamp,
                            updatedAt: timestamp
                        });
                        palletLpnNumberRecord = await db.palletLpnNumbers.get(newId);
                    }

                    if (!palletLpnNumberRecord) continue;

                    // Create or update unit load positions
                    const positionsArray = [];
                    for (const position of unitLoadPositions) {
                        const { id, ...positionData } = position;

                        if (id) {
                            await db.unitLoadPositions.update(id, {
                                ...positionData,
                                updatedAt: timestamp
                            });
                            const updated = await db.unitLoadPositions.get(id);
                            if (updated) positionsArray.push(updated);
                        } else {
                            const newId = await db.unitLoadPositions.add({
                                ...positionData,
                                palletLpnNumberId: palletLpnNumberRecord.id,
                                createdAt: timestamp,
                                updatedAt: timestamp
                            });
                            const created = await db.unitLoadPositions.get(newId);
                            if (created) positionsArray.push(created);
                        }
                    }

                    createdOrUpdatedPositions.push({
                        ...palletLpnNumberRecord,
                        unitLoadPositions: positionsArray
                    });
                }

                return {
                    palletLpnNumbers: createdOrUpdatedPositions
                };
            } catch (error) {
                console.error('Error creating/updating unit load positions:', error);
                throw error;
            }
        });
    }
}
